import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]                         = useState(null);
  const [session, setSession]                   = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [profile, setProfile]                   = useState(null);
  const [profileLoading, setProfileLoading]     = useState(false);
  // Read synchronously from URL hash — avoids race condition with async events
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    () => typeof window !== 'undefined' && window.location.hash.includes('type=recovery')
  );

  /* ── Profile fetch + realtime ──────────────────────────────────────────── */
  const fetchProfile = useCallback(async (userId) => {
    if (!supabase || !userId) { setProfile(null); return; }
    setProfileLoading(true);
    const { data, error } = await supabase
      .from('user_profiles')
      .select('user_id,email,full_name,role,status,approved_at')
      .eq('user_id', userId)
      .maybeSingle();
    setProfileLoading(false);
    if (error) {
      // Most likely the V12 migration hasn't run — treat as pending so the user
      // sees a clear screen instead of crashing.
      console.error('[AuthContext] profile fetch error:', error.message);
      setProfile({ user_id: userId, status: 'pending', role: 'user' });
      return;
    }
    if (!data) {
      // Trigger should have created the row, but fall back gracefully.
      setProfile({ user_id: userId, status: 'pending', role: 'user' });
      return;
    }
    setProfile(data);
  }, []);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) fetchProfile(session.user.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      } else if (event === 'USER_UPDATED') {
        setIsPasswordRecovery(false);
        window.history.replaceState(null, '', window.location.pathname);
      } else if (event === 'SIGNED_OUT') {
        setIsPasswordRecovery(false);
        setProfile(null);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) fetchProfile(session.user.id);
      else setProfile(null);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  /* ── Realtime: react instantly when admin approves/changes the profile ── */
  useEffect(() => {
    if (!supabase || !user?.id) return;
    const channel = supabase
      .channel(`user_profile_${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_profiles', filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (payload.new) setProfile(payload.new);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    supabase,
    isPasswordRecovery,
    setIsPasswordRecovery,
    profile,
    profileLoading,
    isAdmin:    profile?.role === 'admin' && profile?.status === 'active',
    isPending:  !!user && profile?.status === 'pending',
    isInactive: !!user && profile?.status === 'inactive',
    isActive:   !!user && profile?.status === 'active',
    refreshProfile: () => user?.id && fetchProfile(user.id),
  }), [user, session, loading, isPasswordRecovery, profile, profileLoading, fetchProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
